import { Box, Button, FormGroup, Typography, useTheme } from '@mui/material';
import React from 'react';
import { useSetRecoilState } from 'recoil';
import LabeledList from '~/components/LabeledList';
import { workflowModalState, WorkflowTypes } from '~/state/atoms/viewerAtoms';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import ROICheckboxItem from './ROICheckboxItem';

interface StructureSelectionProps {
    selectedModel: PredictionModel | undefined;
    selectedStructures: string[];
    roiNames: string[];
    setSelectedStructures: (structures: string[]) => void;
    workflowType: WorkflowTypes;
}

const StructureSelection: React.FC<StructureSelectionProps> = ({
    selectedModel,
    selectedStructures,
    roiNames,
    setSelectedStructures,
    workflowType,
}) => {
    const theme = useTheme();
    const setWorkflowModalState = useSetRecoilState(workflowModalState);

    const handleROIChange = (roi: string, checked: boolean) => {
        setSelectedStructures(
            checked
                ? [...selectedStructures, roi]
                : selectedStructures.filter((s) => s !== roi)
        );
    };

    const handleSelectAllToggle = () => {
        setSelectedStructures(
            selectedStructures.length === roiNames.length ? [] : roiNames
        );
    };

    return (
        <LabeledList label={`Structures (${selectedStructures.length})`}>
            {selectedModel || roiNames.length > 0 ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                            marginBottom: theme.spacing(4),
                        }}
                    >
                        <FormGroup>
                            {roiNames
                                .sort((a, b) => a.localeCompare(b))
                                .map((roi) => (
                                    <ROICheckboxItem
                                        key={roi}
                                        roi={roi}
                                        checked={selectedStructures.includes(
                                            roi
                                        )}
                                        onChange={(checked) =>
                                            handleROIChange(roi, checked)
                                        }
                                    />
                                ))}
                        </FormGroup>
                    </Box>
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: -4,
                            left: 0,
                            right: 0,
                            padding: theme.spacing(0.5),
                            backgroundColor: theme.palette.background.default,
                        }}
                    >
                        <Button
                            variant="text"
                            fullWidth
                            onClick={handleSelectAllToggle}
                            sx={{ fontSize: 10 }}
                        >
                            {selectedStructures.length === roiNames.length
                                ? 'Unselect All'
                                : 'Select All'}
                        </Button>
                    </Box>
                </Box>
            ) : workflowType !== WorkflowTypes.ADAPT ? (
                <Typography fontSize={10} p={1} textAlign="center">
                    Choose a model to see available structures
                </Typography>
            ) : (
                <Box display="grid" justifyContent="center" alignItems="center">
                    <Typography fontSize={10} p={1} textAlign="center">
                        No structure set available. Do you want to generate
                        structures?
                    </Typography>

                    <Button
                        variant="text"
                        color="primary"
                        size="small"
                        sx={{ fontSize: 12 }}
                        onClick={() => {
                            setWorkflowModalState((state) => ({
                                ...state,
                                page: WorkflowTypes.CONTOUR,
                            }));
                        }}
                    >
                        CONTOUR+
                    </Button>
                </Box>
            )}
        </LabeledList>
    );
};

export default StructureSelection;
